import React, { useState, useEffect } from 'react';
import CreditRepairDashboard from './CreditRepairDashboard';
import IdTheftDashboard from './IdTheftDashboard';
import CreditDevelopmentDashboard from './CreditDevelopmentDashboard';
import DebtSettlementDashboard from './DebtSettlementDashboard';
import CreditManagementDashboard from './CreditManagementDashboard';
import CreditManagementManagerDashboard from './CreditManagementManagerDashboard';
import { ClientDetailPage } from '../detail/ClientDetailPage';
import InternalChatbot from '../../components/common/InternalChatbot';
import { GobodoRequiredGuard } from '../../components/common/GobodoRequiredGuard';
import { CreditRepairClient, IdTheftClient, CreditDevelopmentClient } from '../../types';
import { useApplicationContext } from '../../contexts/ApplicationContext';

interface CreditManagementAppProps {
  onBackToHome?: () => void;
  userRole?: string;
}

const CreditManagementApp: React.FC<CreditManagementAppProps> = ({ onBackToHome, userRole = 'employee' }) => {
  const { setCurrentApplication } = useApplicationContext();
  const [viewMode, setViewMode] = useState(() => {
    // Show manager dashboard for managers and CM supervisors, operations dashboard for others
    return (userRole === 'manager' || userRole === 'cm_supervisor') ? 'manager-dashboard' : 'operations-dashboard';
  });
  const [selectedCreditRepairClient, setSelectedCreditRepairClient] = useState<CreditRepairClient | null>(null);
  const [selectedIdTheftClient, setSelectedIdTheftClient] = useState<IdTheftClient | null>(null);
  const [selectedCreditDevClient, setSelectedCreditDevClient] = useState<CreditDevelopmentClient | null>(null);

  // Set application context based on current viewMode
  useEffect(() => {
    console.log('🎯 CreditManagementApp viewMode changed:', viewMode);

    if (viewMode === 'credit-repair' || viewMode === 'credit-repair-detail') {
      console.log('🎯 Setting application context to CreditRepair');
      setCurrentApplication('CreditRepair');
    } else if (viewMode === 'id-theft' || viewMode === 'id-theft-detail') {
      console.log('🎯 Setting application context to IDTheftRecovery');
      setCurrentApplication('IDTheftRecovery');
    } else if (viewMode === 'credit-development' || viewMode === 'credit-development-detail') {
      console.log('🎯 Setting application context to CreditDevelopment');
      setCurrentApplication('CreditDevelopment');
    } else if (viewMode === 'debt-settlement') {
      console.log('🎯 Setting application context to DebtSettlement');
      setCurrentApplication('DebtSettlement');
    } else {
      // For manager-dashboard and operations-dashboard, keep as Global
      console.log('🎯 Setting application context to Global (dashboard view)');
      setCurrentApplication('Global');
    }

    // Reset to Global when component unmounts
    return () => {
      console.log('🎯 CreditManagementApp unmounting - resetting application context to Global');
      setCurrentApplication('Global');
    };
  }, [viewMode, setCurrentApplication]);

  const handleBackToDashboard = () => {
    // Return to the appropriate dashboard based on user role
    setViewMode((userRole === 'manager' || userRole === 'cm_supervisor') ? 'manager-dashboard' : 'operations-dashboard');
  };

  const handleViewOperations = () => {
    setViewMode('operations-dashboard');
  };

  const handleViewManagerDashboard = () => {
    setViewMode('manager-dashboard');
  };

  const wrap = (children: React.ReactNode) => <GobodoRequiredGuard>{children}</GobodoRequiredGuard>;

  if (viewMode === 'manager-dashboard') {
    return wrap(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <CreditManagementManagerDashboard
          onViewOperations={handleViewOperations}
        />
        <InternalChatbot
          applicationContext="credit_management"
          position="bottom-right"
        />
      </div>
    );
  }

  if (viewMode === 'credit-repair-detail' && selectedCreditRepairClient) {
    return wrap(
      <>
        <ClientDetailPage
          id={selectedCreditRepairClient.id}
          onBack={() => setViewMode('credit-repair')}
        />
        <InternalChatbot
          applicationContext="credit_management"
          clientContext={selectedCreditRepairClient}
          position="bottom-right"
        />
      </>
    );
  }

  if (viewMode === 'credit-repair') {
    return wrap(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <CreditRepairDashboard
          onSelectClient={(client) => {
            setSelectedCreditRepairClient(client);
            setViewMode('credit-repair-detail');
          }}
          onBackToDashboard={handleBackToDashboard}
        />
        <InternalChatbot
          applicationContext="credit_management"
          position="bottom-right"
        />
      </div>
    );
  }


  if (viewMode === 'id-theft-detail' && selectedIdTheftClient) {
    return wrap(
      <>
        <ClientDetailPage
          id={selectedIdTheftClient.id}
          onBack={() => setViewMode('id-theft')}
        />
        <InternalChatbot
          applicationContext="credit_management"
          clientContext={selectedIdTheftClient}
          position="bottom-right"
        />
      </>
    );
  }

  if (viewMode === 'id-theft') {
    return wrap(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <IdTheftDashboard
          onSelectClient={(client) => {
            setSelectedIdTheftClient(client);
            setViewMode('id-theft-detail');
          }}
          onBackToDashboard={handleBackToDashboard}
        />
        <InternalChatbot
          applicationContext="credit_management"
          position="bottom-right"
        />
      </div>
    );
  }

  if (viewMode === 'credit-development-detail' && selectedCreditDevClient) {
    return wrap(
      <>
        <ClientDetailPage
          id={selectedCreditDevClient.id}
          onBack={() => setViewMode('credit-development')}
        />
        <InternalChatbot
          applicationContext="credit_management"
          clientContext={selectedCreditDevClient}
          position="bottom-right"
        />
      </>
    );
  }

  if (viewMode === 'credit-development') {
    return wrap(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <CreditDevelopmentDashboard
          onSelectClient={(client) => {
            setSelectedCreditDevClient(client);
            setViewMode('credit-development-detail');
          }}
          onBackToDashboard={handleBackToDashboard}
        />
        <InternalChatbot
          applicationContext="credit_management"
          position="bottom-right"
        />
      </div>
    );
  }

  if (viewMode === 'debt-settlement') {
    return wrap(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <DebtSettlementDashboard
          onBackToDashboard={handleBackToDashboard}
        />
        <InternalChatbot
          applicationContext="credit_management"
          position="bottom-right"
        />
      </div>
    );
  }

  return wrap(
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <CreditManagementDashboard
        onNavigate={(view) => setViewMode(view)}
        userRole={userRole}
        onViewManagerDashboard={handleViewManagerDashboard}
      />
      <InternalChatbot
        applicationContext="credit_management"
        position="bottom-right"
      />
    </div>
  );
};

export default CreditManagementApp;